import React, { useEffect, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { ProgressSpinner } from "primereact/progressspinner";
import axiosInstance from "../utils/axios";
import toast from "react-hot-toast";
import TinyMCEEditor from "../components/QuillEditor";

const AgentSponsorsTab = () => {
  const [sponsors, setSponsors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [editorModalVisible, setEditorModalVisible] = useState(false);
  const [editingSponsor, setEditingSponsor] = useState(null);
  const [newName, setNewName] = useState("");
  const [editorContent, setEditorContent] = useState("");
  const [saving, setSaving] = useState(false);

  const fetchSponsors = () => {
    setLoading(true);
    axiosInstance
      .get("/api/sponsors")
      .then((res) => setSponsors(Array.isArray(res.data) ? res.data : []))
      .catch((err) => {
        console.error(err);
        toast.error("Failed to load sponsors");
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => fetchSponsors(), []);

  const handleAddSponsor = () => {
    if (!newName.trim()) {
      toast.error("Sponsor name is required");
      return;
    }
    setSaving(true);
    axiosInstance
      .post("/api/sponsors", { name: newName.trim() })
      .then(() => {
        toast.success("Sponsor added");
        setNewName("");
        setAddModalVisible(false);
        fetchSponsors();
      })
      .catch((err) => {
        toast.error(err?.response?.data?.error || "Failed to add sponsor");
      })
      .finally(() => setSaving(false));
  };

  const openEditor = (sponsor) => {
    setEditingSponsor(sponsor);
    setEditorContent(sponsor.description || "");
    setEditorModalVisible(true);
  };

  const saveDescription = () => {
    if (!editingSponsor) return;
    setSaving(true);
    axiosInstance
      .put(`/api/sponsors/${editingSponsor.id}`, { description: editorContent })
      .then(() => {
        toast.success("Sponsor updated");
        setEditorModalVisible(false);
        setEditingSponsor(null);
        fetchSponsors();
      })
      .catch((err) => {
        toast.error(err?.response?.data?.error || "Failed to update sponsor");
      })
      .finally(() => setSaving(false));
  };

  const handleDelete = (e, sponsor) => {
    e.stopPropagation();
    if (!window.confirm(`Delete sponsor "${sponsor.name}"?`)) return;
    axiosInstance
      .delete(`/api/sponsors/${sponsor.id}`)
      .then(() => {
        toast.success("Sponsor deleted");
        fetchSponsors();
      })
      .catch((err) => {
        toast.error(err?.response?.data?.error || "Failed to delete sponsor");
      });
  };

  return (
    <div className="p-5">
      <div className="flex justify-between items-center mb-5">
        <h1 className="lg:text-2xl font-semibold text-[#003049]">Sponsors</h1>
        <Button
          label="Add Sponsor"
          icon="pi pi-plus"
          className="bg-[#BA1500] text-white btn text-sm focus:outline-none focus:ring-0"
          onClick={() => {
            setNewName("");
            setAddModalVisible(true);
          }}
        />
      </div>

      <DataTable
        value={sponsors}
        loading={loading}
        emptyMessage="No sponsors yet. Add one to get started."
        responsiveLayout="scroll"
        className="text-xs sm:text-sm"
        rowClassName="cursor-pointer"
        onRowClick={(e) => openEditor(e.data)}
      >
        <Column
          header="Actions"
          body={(row) => (
            <div className="flex gap-2">
              <Button
                label="Edit"
                className="p-button-text p-button-sm"
                onClick={(e) => {
                  e.stopPropagation();
                  openEditor(row);
                }}
              />
              <Button
                icon="pi pi-trash"
                className="p-button-text p-button-danger p-button-sm"
                onClick={(e) => handleDelete(e, row)}
              />
            </div>
          )}
          style={{ width: 120 }}
        />
        <Column
          field="name"
          header="Sponsor Name"
          sortable
          body={(row) => (
            <span className="font-medium text-[#003049] hover:underline">
              {row.name}
            </span>
          )}
        />
      </DataTable>

      {/* Add Sponsor Modal */}
      <Dialog
        header="Add Sponsor"
        visible={addModalVisible}
        onHide={() => setAddModalVisible(false)}
        className="w-full max-w-md"
        footer={
          <div className="flex justify-end gap-2">
            <Button
              label="Cancel"
              onClick={() => setAddModalVisible(false)}
              className="p-button-text"
            />
            <Button
              label="Add"
              onClick={handleAddSponsor}
              loading={saving}
              disabled={saving || !newName.trim()}
              className="bg-[#BA1500] text-white"
            />
          </div>
        }
      >
        <div className="flex flex-col gap-3 py-2">
          <label className="font-medium">Sponsor Name</label>
          <InputText
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Enter sponsor name"
            className="w-full"
          />
        </div>
      </Dialog>

      {/* TinyMCE Editor Modal - Write about sponsor */}
      <Dialog
        header={editingSponsor ? `Write about: ${editingSponsor.name}` : ""}
        visible={editorModalVisible}
        onHide={() => setEditorModalVisible(false)}
        className="w-full max-w-4xl"
        style={{ width: "90vw" }}
        footer={
          <div className="flex justify-end gap-2">
            <Button
              label="Cancel"
              onClick={() => setEditorModalVisible(false)}
              className="p-button-text"
            />
            <Button
              label="Save"
              onClick={saveDescription}
              loading={saving}
              disabled={saving}
              className="bg-[#BA1500] text-white"
            />
          </div>
        }
      >
        {editorModalVisible && (
          <div className="py-2 min-h-[300px]">
            <TinyMCEEditor
              value={editorContent}
              onChange={setEditorContent}
              height={400}
            />
          </div>
        )}
      </Dialog>
    </div>
  );
};

export default AgentSponsorsTab;
